import React, { useMemo, useState, ReactElement } from 'react';
import { LocationBreakdownRow } from '@Apis/companyDashboard';
import { scoreBand } from './Tiles/ScoreBlock';
import { BreakdownMetric, BREAKDOWN_METRIC_LABELS } from './breakdownSelection';
import BreakdownProgressBar from './BreakdownProgressBar';
import { useScoreInfoTrigger } from '../ScoreInfo/ScoreInfoModal';
import './LocationBreakdownTable.scss';

// The sortable columns, in render order. Column order and visibility are fixed — there is no
// user customization in this story.
export type BreakdownSortKey =
    | 'groupName'
    | 'healthScore'
    | 'riskScore'
    | 'overallCompletionPct'
    | 'pastDueMilestoneCount'
    | 'upcomingMilestoneCount'
    | 'totalUnassignedCount';

export type SortDirection = 'asc' | 'desc';

interface ColumnDef {
    key: BreakdownSortKey;
    label: string;
    isNumeric: boolean;
    isMetric: boolean;
    width: string;
    defaultDirection?: SortDirection;
    /** Set on the count columns: which task list the number in this cell opens. */
    drillMetric?: BreakdownMetric;
    /** Colours a non-zero count red, the way the Past-Due column reads at a glance. */
    isDanger?: boolean;
}

// Widths total 100%. The count columns are wider than their numbers need so that a wrapped
// two-word header fits, but no wider: the panel looked scattered when a 44% Progress column
// pushed them apart from the name they belong to.
const COLUMNS: ColumnDef[] = [
    { key: 'groupName', label: 'Name', isNumeric: false, isMetric: false, width: '24%' },
    { key: 'healthScore', label: 'Health', isNumeric: true, isMetric: true, width: '7%' },
    { key: 'riskScore', label: 'Risk', isNumeric: true, isMetric: true, width: '7%' },
    {
        key: 'overallCompletionPct',
        label: 'Progress',
        isNumeric: true,
        isMetric: false,
        width: '34%',
        defaultDirection: 'desc',
    },
    {
        key: 'pastDueMilestoneCount',
        label: 'Past-Due Milestones',
        isNumeric: true,
        isMetric: true,
        width: '10%',
        drillMetric: 'pastDueMilestones',
        isDanger: true,
    },
    {
        key: 'upcomingMilestoneCount',
        label: 'Upcoming Deadlines',
        isNumeric: true,
        isMetric: true,
        width: '9%',
        drillMetric: 'upcomingDeadlines',
    },
    {
        key: 'totalUnassignedCount',
        label: 'Unassigned Work',
        isNumeric: true,
        isMetric: true,
        width: '9%',
        drillMetric: 'unassignedWork',
    },
];

// The clickable count columns, in render order. Derived from COLUMNS rather than listed again
// so a column can never be rendered under one header and drilled into as another.
const COUNT_COLUMNS = COLUMNS.filter(
    (column): column is ColumnDef & { drillMetric: BreakdownMetric } => column.drillMetric !== undefined
);

// Shown in place of the empty name the endpoint sends for the "no value for this grouping"
// bucket. Exported so the sidebar and its tests can name the same thing the table does.
export const UNASSIGNED_GROUP_NAME = 'Unassigned';

export const DEFAULT_SORT_KEY: BreakdownSortKey = 'groupName';
export const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

// Nothing to report reads as an em dash, not as a zero: a column of 0s draws the eye to the
// groups with no work in it. Sorting still uses the real number.
const formatCount = (count: number): string | number => (count ? count : '—');

// The number the Progress column actually sorts on, shown beside the bar the way the location
// tile shows it beside its own (see LocationSummaryTile). Without it the sort looks broken: the
// endpoint's percentage excludes Not Applicable tasks where the bar sizes every segment against
// the full total, so a group with a lot of excluded work outranks one whose green segment is
// visibly wider. Rounded, because the column has to fit it beside the bar.
const formatPercent = (percent?: number | null): string =>
    typeof percent === 'number' && Number.isFinite(percent) ? `${Math.round(percent)}%` : '—';

const numericValue = (row: LocationBreakdownRow, key: BreakdownSortKey): number | null => {
    const value = row[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

/**
 * Orders the rows by one column. A missing score is not a low score, so unscored groups sink
 * to the bottom in both directions rather than pretending to be a 0 or a 10; ties fall back to
 * the name so the order is stable no matter which column is sorted.
 */
const sortRows = (
    rows: LocationBreakdownRow[],
    key: BreakdownSortKey,
    direction: SortDirection,
    isNumeric: boolean
): LocationBreakdownRow[] => {
    const sign = direction === 'asc' ? 1 : -1;
    const byName = (a: LocationBreakdownRow, b: LocationBreakdownRow) =>
        (a.groupName ?? '').localeCompare(b.groupName ?? '', undefined, { sensitivity: 'base' });

    return [...rows].sort((a, b) => {
        if (!isNumeric) return sign * byName(a, b);

        const left = numericValue(a, key);
        const right = numericValue(b, key);
        if (left === null && right === null) return byName(a, b);
        if (left === null) return 1;
        if (right === null) return -1;
        if (left === right) return byName(a, b);
        return sign * (left - right);
    });
};

interface ScoreBadgeProps {
    label: string;
    score?: number | null;
    unavailableReason?: string | null;
    // Health is higher-is-better; Risk is a magnitude (1 low, 10 high) and inverts the bands.
    higherIsBetter: boolean;
}

// The compact form of the location row's ScoreBlock: same bands, same colours, no label —
// the column header already names the metric.
const ScoreBadge = ({ label, score, unavailableReason, higherIsBetter }: ScoreBadgeProps): ReactElement => {
    const numericScore = typeof score === 'number' && Number.isFinite(score) ? score : null;
    const { className: triggerClassName = '', ...triggerProps } = useScoreInfoTrigger({ label, score: numericScore, unavailableReason });

    if (numericScore === null) {
        return (
            <span
                {...triggerProps}
                className={`lbt-score lbt-score--placeholder ${triggerClassName}`}
                title={unavailableReason ?? undefined}>
                —
            </span>
        );
    }

    return (
        <span {...triggerProps} className={`lbt-score lbt-score--${scoreBand(numericScore, higherIsBetter)} ${triggerClassName}`}>
            {numericScore.toFixed(1)}
        </span>
    );
};

interface LocationBreakdownTableProps {
    rows: LocationBreakdownRow[];
    /** Names the grouping in the caption, e.g. "Discipline". */
    groupLabel: string;
    /**
     * Drills into one group's tasks, narrowed to the value that was clicked — the group name
     * itself opens the whole group ('all'). Omitted, every value renders as plain text.
     */
    onGroupSelect?: (row: LocationBreakdownRow, metric: BreakdownMetric) => void;
}

/**
 * One count cell. A zero renders as the em dash the story asks for and is deliberately *not*
 * clickable: a drill-down that can only ever say "No tasks found" is a control that promises
 * something it cannot deliver, and the Progress bar already sets that precedent — a status with
 * no tasks gets no segment to click either.
 */
interface CountCellProps {
    value: number;
    /** The column's own header, so the screen-reader label names what was clicked. */
    label: string;
    groupName: string;
    isDanger?: boolean;
    onSelect?: () => void;
}

const CountCell = ({ value, label, groupName, isDanger, onSelect }: CountCellProps): ReactElement => (
    <td className={`lbt-col--numeric${isDanger && value ? ' lbt-count--danger' : ''}`}>
        {onSelect && value > 0 ? (
            <button
                type='button'
                className='lbt-count__link'
                aria-label={`Show the ${value} ${label} tasks for ${groupName}`}
                onClick={onSelect}>
                {value}
            </button>
        ) : (
            formatCount(value)
        )}
    </td>
);

const LocationBreakdownTable = ({ rows, groupLabel, onGroupSelect }: LocationBreakdownTableProps): ReactElement => {

    const [sortKey, setSortKey] = useState<BreakdownSortKey>(DEFAULT_SORT_KEY);
    const [sortDirection, setSortDirection] = useState<SortDirection>(DEFAULT_SORT_DIRECTION);

    // The endpoint sends `groupName: null` for the bucket of work with no value for the
    // current grouping, which the schema turns into '' (see nullableText in
    // companyDashboard.types.ts). An empty name used to be a blank cell; now that the name is
    // the drill-in control it would be a zero-width button that a mouse cannot hit and a
    // screen reader announces as a bare "button". Substituted here rather than at render so
    // the row sorts under the name it displays, and so the selection handed to the sidebar
    // carries it too.
    const namedRows = useMemo(
        () => rows.map((row) => (row.groupName ? row : { ...row, groupName: UNASSIGNED_GROUP_NAME })),
        [rows]
    );

    const sortedRows = useMemo(() => {
        const column = COLUMNS.find((candidate) => candidate.key === sortKey) ?? COLUMNS[0];
        return sortRows(namedRows, column.key, sortDirection, column.isNumeric);
    }, [namedRows, sortKey, sortDirection]);

    const onHeaderClick = (column: ColumnDef) => {
        if (column.key === sortKey) {
            setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
            return;
        }
        // A new column starts ascending — A-Z for the name, smallest-first for the counts —
        // unless it names its own useful first answer, as Progress does.
        setSortKey(column.key);
        setSortDirection(column.defaultDirection ?? 'asc');
    };

    return (
        // The scroll box is what makes the header sticky: `position: sticky` resolves against
        // the nearest scrolling ancestor, and the panel itself does not scroll.
        <div className='lbt-scroll'>
            <table className='lbt-table'>
                <caption>Breakdown by {groupLabel}</caption>
                <colgroup>
                    {COLUMNS.map((column) => (
                        <col key={column.key} style={{ width: column.width }} />
                    ))}
                </colgroup>
                <thead>
                    <tr>
                        {COLUMNS.map((column) => {
                            const isSorted = column.key === sortKey;
                            return (
                                <th
                                    key={column.key}
                                    scope='col'
                                    className={column.isMetric ? 'lbt-col--numeric' : undefined}
                                    aria-sort={isSorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                    <button
                                        type='button'
                                        className={`lbt-sort${isSorted ? ' lbt-sort--active' : ''}`}
                                        onClick={() => onHeaderClick(column)}>
                                        <span className='lbt-sort__label'>{column.label}</span>
                                        {/* Carets, not arrows: fa-arrow-up/down are not in the
                                            bundled FA6 subset and render as tofu. */}
                                        <i
                                            className={`fa-solid fa-caret-${isSorted && sortDirection === 'desc' ? 'down' : 'up'} lbt-sort__icon`}
                                            aria-hidden='true'
                                        />
                                    </button>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map((row) => (
                        <tr key={row.groupId}>
                            <td className='lbt-name' title={row.groupName}>
                                {onGroupSelect ? (
                                    <button
                                        type='button'
                                        className='lbt-name__link'
                                        onClick={() => onGroupSelect(row, 'all')}>
                                        {row.groupName}
                                    </button>
                                ) : (
                                    row.groupName
                                )}
                            </td>
                            <td className='lbt-col--numeric'>
                                <ScoreBadge
                                    label='Health'
                                    score={row.healthScore}
                                    unavailableReason={row.scoresUnavailableReason}
                                    higherIsBetter
                                />
                            </td>
                            <td className='lbt-col--numeric'>
                                <ScoreBadge
                                    label='Risk'
                                    score={row.riskScore}
                                    unavailableReason={row.scoresUnavailableReason}
                                    higherIsBetter={false}
                                />
                            </td>
                            <td className='lbt-progress'>
                                <div className='lbt-progress__wrap'>
                                    <BreakdownProgressBar
                                        taskGraph={row.taskGraph ?? []}
                                        groupName={row.groupName}
                                        onSegmentSelect={
                                            onGroupSelect ? (metric) => onGroupSelect(row, metric) : undefined
                                        }
                                    />
                                    <span className='lbt-progress__pct'>{formatPercent(row.overallCompletionPct)}</span>
                                </div>
                            </td>
                            {COUNT_COLUMNS.map((column) => (
                                <CountCell
                                    key={column.key}
                                    value={numericValue(row, column.key) ?? 0}
                                    label={BREAKDOWN_METRIC_LABELS[column.drillMetric]}
                                    groupName={row.groupName}
                                    isDanger={column.isDanger}
                                    onSelect={
                                        onGroupSelect ? () => onGroupSelect(row, column.drillMetric) : undefined
                                    }
                                />
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default LocationBreakdownTable;
